import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Sellable collection: manual load-more pagination (24 per page) + filter skeleton state.
 */
class NfSellableCollection {
  /** @type {boolean} */
  #isLoading = false;

  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.sectionId = root.getAttribute('section-id');
    this.#bindLoadMore();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
  }

  /** @returns {HTMLUListElement | null} */
  #getGrid() {
    const grid = this.root.querySelector('[data-nf-product-grid]');
    return grid instanceof HTMLUListElement ? grid : null;
  }

  #bindLoadMore() {
    const button = this.root.querySelector('[data-nf-load-more]');
    if (!button || !this.sectionId) return;

    if (button instanceof HTMLButtonElement) {
      this.#storeLoadMoreDefaultLabel(button);
    }

    button.addEventListener('click', () => {
      this.#loadNextPage();
    });
  }

  async #loadNextPage() {
    if (this.#isLoading) return;

    const grid = this.#getGrid();
    if (!grid || !this.sectionId) return;

    const lastPage = Number(grid.dataset.lastPage);
    const cards = grid.querySelectorAll('.product-grid__item');
    const lastCard = cards[cards.length - 1];
    if (!lastCard) return;

    const currentPage = Number(lastCard.dataset.page);
    const nextPage = currentPage + 1;

    if (!lastPage || nextPage > lastPage) {
      this.#hideLoadMore();
      return;
    }

    this.#isLoading = true;
    const button = this.root.querySelector('[data-nf-load-more]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const loadingLabel = button.dataset.loadingLabel;
      if (loadingLabel) {
        button.setAttribute('aria-label', loadingLabel);
      }
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(nextPage));
      url.searchParams.delete('section_id');
      url.hash = '';

      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const nextGrid = parsed.querySelector('[data-nf-product-grid]');
      const nextItems = nextGrid?.querySelectorAll('.product-grid__item');

      if (!nextItems?.length) return;

      grid.append(...nextItems);
      history.pushState('', '', url.toString());

      if (nextPage >= lastPage) {
        this.#hideLoadMore();
      }
    } finally {
      this.#isLoading = false;
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (button.dataset.defaultLabel) {
          button.setAttribute('aria-label', button.dataset.defaultLabel);
        }
      }
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #storeLoadMoreDefaultLabel(button) {
    if (!button.dataset.defaultLabel) {
      button.dataset.defaultLabel = button.getAttribute('aria-label') || '';
    }
  }

  #hideLoadMore() {
    const container = this.root.querySelector('[data-nf-load-more-container]');
    if (container instanceof HTMLElement) {
      container.hidden = true;
    }
  }

  #onFilterUpdate = () => {
    this.root.classList.add('is-loading');

    const grid = this.#getGrid();
    if (!grid) {
      this.root.classList.remove('is-loading');
      return;
    }

    const observer = new MutationObserver(() => {
      this.root.classList.remove('is-loading');
      observer.disconnect();
    });

    observer.observe(grid, { childList: true, subtree: true });
  };
}

document.querySelectorAll('[data-nf-collection]').forEach((root) => {
  if (root instanceof HTMLElement) {
    new NfSellableCollection(root);
  }
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const root = target.querySelector('[data-nf-collection]');
  if (root instanceof HTMLElement) {
    new NfSellableCollection(root);
  }
});
